package co.torvus.web.content

/** Static content for the Torvus marketing site — no API, all hard-coded from the design. */

data class NavItem(val label: String, val id: String)

val navItems: List<NavItem> = listOf(
    NavItem("Home", "home"),
    NavItem("Services", "services"),
    NavItem("Contact", "contact"),
)

data class HomeBlock(val title: String, val paragraphs: List<String>)
data class HomeSlide(val hero: Boolean, val blocks: List<HomeBlock>)

val homeSlides: List<HomeSlide> = listOf(
    HomeSlide(
        hero = true,
        blocks = listOf(
            HomeBlock(
                title = "Most fitness apps hand you a workout. Torvus hands you the tools.",
                paragraphs = listOf(
                    "Torvus is where trainers and gyms manage clients, program workouts, and run events — " +
                            "and where athletes train, log, and see their programming the way it was actually written. " +
                            "You bring the expertise. Torvus gives it structure.",
                    "Built for professionals — and simple enough for anyone who's outgrown the app that trains " +
                            "everyone the same way.",
                ),
            ),
        ),
    ),
    HomeSlide(
        hero = false,
        blocks = listOf(
            HomeBlock(
                title = "About Torvus",
                paragraphs = listOf("Torvus is launching soon. What follows is what we're building, and why."),
            ),
            HomeBlock(
                title = "Why We Started",
                paragraphs = listOf(
                    "Torvus is a fitness coaching platform built by people who thought the existing tools got it " +
                            "backwards.",
                    "Most fitness apps hand you a workout. They decide the sets, the progression, the plan — and the " +
                            "coach, if there is one, works around the software. Torvus starts from the opposite assumption: " +
                            "that the person doing the programming knows what they're doing. Our job isn't to replace their " +
                            "judgment with a template. It's to give that judgment the fastest, cleanest tools we can build.",
                ),
            ),
        ),
    ),
    HomeSlide(
        hero = false,
        blocks = listOf(
            HomeBlock(
                title = "What We're Building",
                paragraphs = listOf(
                    "That shows up everywhere in the product. At its core is a workout builder tied directly to a " +
                            "structured exercise library, so programming is quick without ever being generic — with " +
                            "dedicated tools for the specific modalities real training is built from, so the way you " +
                            "structure a session is never flattened to fit the software. Client and gym management that " +
                            "holds an entire operation — coaches, members, individual clients — in one place. Event " +
                            "scheduling that works the same whether you're a solo trainer or a full facility. Every feature " +
                            "assumes a professional is in the driver's seat, while staying simple enough that a motivated " +
                            "amateur never hits a wall.",
                ),
            ),
        ),
    ),
    HomeSlide(
        hero = false,
        blocks = listOf(
            HomeBlock(
                title = "Who We Are",
                paragraphs = listOf(
                    "We're a small team: two developers and a sport-science background, obsessive about getting the " +
                            "details right rather than shipping the widest possible feature list. Torvus is the result — " +
                            "deliberately focused, built to be relied on daily by the people whose living depends on it.",
                ),
            ),
            HomeBlock(
                title = "Talk to Us",
                paragraphs = listOf(
                    "Torvus is a registered business in Zagreb, Croatia. If you want to talk to us — about the product, " +
                            "a partnership, or your gym — we read every message.",
                ),
            ),
        ),
    ),
)

data class Service(val title: String, val body: String, val image: String)

val services: List<Service> = listOf(
    Service(
        "Session Creation",
        "You can create workout sessions, assign groups and individual clients to them.",
        "images/services/session-creation.png",
    ),
    Service(
        "Workout Creation",
        "Each session can have its own workout. Our builder gives you a quick, flexible way to turn your " +
                "programming into clear, structured sessions your clients can follow.",
        "images/services/workout-creation.png",
    ),
    Service(
        "Extensive Exercise Library",
        "Our library includes over 1,000 exercises to build your workouts from — and if it's ever missing " +
                "something you need, you can add your own.",
        "images/services/exercise-picker.png",
    ),
    Service(
        "Client History",
        "Workout ratings can be seen within the client history, so trainers can review past user ratings before " +
                "editing or creating future workouts.",
        "images/services/client-history.png",
    )
)

